import ReactSelect, { components, GroupBase, Props as ReactSelectProps } from 'react-select'
import { forwardRef, ReactNode } from 'react'
import { faChevronDown, faTimes, faTimesCircle } from '@fortawesome/pro-regular-svg-icons'
import { twMerge } from 'tailwind-merge'
import { Merge } from '../../util/types'
import { Button } from '../Button/Button'
import { Icon } from '../Icon/Icon'
import { Checkbox } from '../Checkbox/Checkbox'

export interface CoreSelectProps {
  label?: ReactNode
  icon?: ReactNode
  unit?: ReactNode
  description?: ReactNode
  error?: ReactNode
  className?: string | undefined
  disabled?: boolean
  id?: string
  wrapTags?: boolean
  visibleTagsCount?: number
  nonVisibleTagsLabel?: string
}

export type SelectProps<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = Merge<
ReactSelectProps<Option, IsMulti, Group>,
CoreSelectProps
>

const disableStylesProxy = new Proxy({}, {
  get: (_, property) => {
    if (typeof property !== 'string' || !property.startsWith('menu')) {
      return () => {}
    }

    return (baseStyles: any) => baseStyles
  }
})

export const Select = forwardRef(
  function Select<
    Option = unknown,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
  > (props: SelectProps<Option, IsMulti, Group>, ref: any) {
    const {
      label,
      icon,
      unit,
      description,
      error,
      className,
      wrapTags = true,
      visibleTagsCount,
      nonVisibleTagsLabel,
      ...inputProps
    } = props

    const conditionalProps: {
      isDisabled?: boolean
      inputId?: string
    } = {}
    if ('id' in props) { conditionalProps.inputId = props.id }
    if ('disabled' in props) { conditionalProps.isDisabled = props.disabled }

    return (
      <ReactSelect
        isClearable
        classNamePrefix='core-select'
        hideSelectedOptions={false}
        closeMenuOnSelect={inputProps.isMulti !== true}
        ref={ref}
        menuPlacement='auto'
        {...conditionalProps}
        {...inputProps}
        styles={disableStylesProxy}
        components={{
          Input: props => <components.Input {...props} className='w-full flex-1' />,
          SelectContainer: ({ children, innerProps }) => {
            return (
              <div
                {...innerProps}
                className={twMerge('relative space-y-1 text-black', className)}
              >
                {Boolean(label) && (
                  <div className='typo-body-medium font-medium text-gray-600'>
                    {label}
                  </div>
                )}
                {children}

                {Boolean(description) && (
                  <div className='typo-caption font-medium text-gray-600'>
                    {description}
                  </div>
                )}

                {Boolean(error) && (
                  <div className='typo-caption font-medium text-red-500'>
                    {error}
                  </div>
                )}
              </div>
            )
          },
          Control: ({ children, isFocused, menuIsOpen, innerProps, innerRef }) => {
            return (
              <div
                ref={innerRef}
                className={twMerge(
                  'flex h-9 min-h-[2.25rem] cursor-pointer overflow-hidden rounded border-2 border-gray-200 pl-3 hover:border-black',
                  Boolean(error) && 'border-red-500',
                  Boolean(props.isMulti) && 'h-auto',
                  (isFocused || menuIsOpen) && 'border-black'
                )}
                {...innerProps}
              >
                {children}
              </div>
            )
          },
          ClearIndicator: ({ innerProps }) => (
            <div {...innerProps}>
              <Button
                variant='plain'
                className='flex h-full w-8 items-center justify-center text-gray-600'
              >
                <Icon icon={faTimesCircle} />
              </Button>
            </div>
          ),
          ValueContainer: ({ children, innerProps, hasValue }) => {
            if (!hasValue || inputProps.isMulti !== true || (inputProps.isMulti === true && visibleTagsCount === undefined)) {
              return (
                <div
                  className={twMerge(
                    'typo-body scrollbar-hide flex w-2 flex-1 items-center overflow-scroll',
                    Boolean(props.isMulti) && Boolean(wrapTags) && 'flex-wrap',
                    Boolean(props.isMulti) && hasValue && 'pb-1'
                  )}
                  {...innerProps}
                >
                  {children}
                </div>
              )
            }

            // @ts-expect-error
            const [tags, otherChildren] = children
            const overflowCounter: number = tags.slice(visibleTagsCount).length
            const visibleTags = tags.slice(0, visibleTagsCount)

            return (
              <div
                className={twMerge(
                  'typo-body scrollbar-hide flex w-2 flex-1 items-center overflow-scroll',
                  Boolean(props.isMulti) && Boolean(wrapTags) && 'flex-wrap',
                  Boolean(props.isMulti) && hasValue && 'pb-1'
                )}
                {...innerProps}
              >
                {visibleTags}

                {/* The negative margin compensates the padding of the tags to be centered */}
                {overflowCounter > 0 && <span className='mb-[-0.25rem] mr-1'>{`${Number(visibleTagsCount) > 0 ? '+ ' : ''}${overflowCounter} ${nonVisibleTagsLabel ?? ''}`}</span>}

                {otherChildren}
              </div>
            )
          },
          IndicatorSeparator: () => null,
          DropdownIndicator: () => (
            <Button
              variant='plain'
              className='flex h-full w-8 items-center justify-center text-black'
            >
              <Icon icon={faChevronDown} />
            </Button>
          ),
          MenuList: ({
            children,
            innerProps
          }) => {
            return (
              <div
                className='max-h-[300px] overflow-y-auto py-3'
                {...innerProps}
              >
                {children}
              </div>
            )
          },
          IndicatorsContainer: ({ children, innerProps }) => {
            return (
              <div
                className='flex'
                {...innerProps}
              >
                {children}
              </div>
            )
          },
          Menu: ({ children, innerProps, innerRef, getStyles, ...props }) => {
            const styles = getStyles('menu', { children, innerProps, innerRef, getStyles, ...props }) as any

            return (
              <div
                ref={innerRef}
                className={twMerge(
                  'mt-2 w-full rounded bg-white'
                )}
                style={{
                  ...styles,
                  boxShadow: '0px 2px 1px rgba(23, 25, 33, 0.04), 0px 2px 8px rgba(23, 25, 33, 0.12)'
                }}
                {...innerProps}
              >
                {children}
              </div>
            )
          },
          Option: ({ children, innerProps, isFocused, isMulti, isSelected }) => (
            <div
              className={twMerge(
                'typo-body flex cursor-pointer items-center px-3 py-2 hover:bg-gray-50',
                isFocused && 'bg-gray-50',
                isSelected && 'bg-gray-100 hover:bg-gray-100'
              )}
              {...innerProps}
            >
              {isMulti && (
                <div
                  className='ml-1 mr-3 select-none'
                  onClick={e => e.preventDefault()}
                >
                  <Checkbox readOnly status={isSelected ? 'checked' : 'unchecked'} />
                </div>
              )}
              {children}
            </div>
          ),
          SingleValue: ({ children, innerProps }) => (
            <div
              className='truncate'
              {...innerProps}
            >
              {children}
            </div>
          ),
          MultiValue: ({ children, innerProps, removeProps }) => (
            <div
              className='mr-1 mt-1 flex h-6 max-w-[50%] items-center gap-1 rounded bg-gray-200 pl-2'
              {...innerProps}
            >
              <div className='truncate'>
                {children}
              </div>
              <div
                {...removeProps}
                className='flex h-full w-6 items-center justify-center rounded border-2 border-gray-200 p-0.5 hover:bg-gray-300 hover:text-black'
              >
                <Icon icon={faTimes} />
              </div>
            </div>
          ),
          GroupHeading: ({ children }) => (
            <div className='typo-subheading px-3 uppercase text-gray-600'>
              {children}
            </div>
          ),
          ...props.components
        }}
      />
    )
  }
)
